import { get, post, put, del } from './client'
import type { Channel, ChannelBalanceAdjustment, ChannelCostLog, ChannelOpsOverview } from '@/types'
import { computeLowBalanceLevel } from '@/utils/channelBalance'

/** 列表与搜索共用；分页使用后端识别的 `p`（在请求函数内映射） */
export interface ChannelListParams {
  page?: number
  page_size?: number
  id_sort?: boolean
  tag_mode?: boolean
  /** 与 new-api parseStatusFilter 对齐 */
  status?: 'all' | 'enabled' | 'disabled'
  /** 渠道类型；不传或负数表示全部 */
  type?: number
  keyword?: string
  group?: string
  model?: string
  /** 服务端低余额筛选（跨页） */
  low_balance?: boolean
}

export type SearchChannelsParams = ChannelListParams

export interface ChannelListResponse {
  items: Channel[]
  total: number
  page: number
  page_size: number
  /** 各类型数量，含聚合键 all */
  type_counts?: Record<string, number>
}

export interface CreateChannelParams {
  name: string
  type: number
  key: string
  base_url?: string
  /** 表单多选为 string[]；发给后端需为逗号分隔 string（与 new-api `Channel.models` 一致） */
  models: string[] | string
  model_mapping?: Record<string, string>
  status?: number
  priority?: number
  weight?: number
  group?: string
  tag?: string
  balance_source?: string
  balance_currency?: string
  estimated_balance_micro_usd?: number
  balance_warning_threshold_micro_usd?: number
  balance_critical_threshold_micro_usd?: number
  balance_alert_enabled?: boolean
  balance_alert_webhook_url?: string
}

export interface FetchChannelModelsByConfigParams {
  type: number
  key: string
  base_url?: string
}

/** 后端 `models` 为逗号分隔字符串，不可发 JSON 数组 */
export function serializeChannelModels(models: string[] | string | undefined): string {
  if (models === undefined || models === null) return ''
  if (Array.isArray(models)) {
    return models.map((m) => String(m).trim()).filter(Boolean).join(',')
  }
  return String(models).trim()
}

/** 列表/详情的 `models` 在 JSON 中多为逗号分隔字符串，表单多选需 string[] */
export function parseChannelModels(models: unknown): string[] {
  if (Array.isArray(models)) {
    return models.map((m) => String(m).trim()).filter(Boolean)
  }
  if (typeof models === 'string' && models.trim()) {
    return models.split(',').map((s) => s.trim()).filter(Boolean)
  }
  return []
}

function normalizeModelList(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map((item) => {
      if (typeof item === 'string') return item
      if (item && typeof item === 'object' && 'id' in item) return String((item as { id?: unknown }).id ?? '')
      if (item && typeof item === 'object' && 'model_name' in item) {
        return String((item as { model_name?: unknown }).model_name ?? '')
      }
      return ''
    })
    .map((item) => item.trim())
    .filter(Boolean)
}

function num(v: unknown, fallback = 0): number {
  if (v === null || v === undefined || v === '') return fallback
  const n = Number(v)
  return Number.isFinite(n) ? n : fallback
}

function normalizeTypeCounts(raw?: Record<string, number>): Record<string, number> | undefined {
  if (!raw || typeof raw !== 'object') return undefined
  const out: Record<string, number> = {}
  let sum = 0
  for (const [k, v] of Object.entries(raw)) {
    const c = Number(v)
    out[String(k)] = c
    sum += c
  }
  out.all = sum
  return out
}

/** 将 new-api 返回的 snake_case 渠道行转为前端 Channel */
export function normalizeChannelRow(row: Record<string, unknown>): Channel {
  const tagVal = row.tag
  const tag =
    tagVal !== null && tagVal !== undefined && String(tagVal).trim() !== ''
      ? String(tagVal)
      : undefined

  const baseUrlRaw = row.base_url ?? row.baseUrl
  const baseUrl = baseUrlRaw != null && baseUrlRaw !== '' ? String(baseUrlRaw) : ''

  const modelMappingRaw = row.model_mapping ?? row.modelMapping
  const channelInfoRaw = row.channel_info ?? row.channelInfo

  const channel: Channel = {
    id: num(row.id),
    name: String(row.name ?? ''),
    type: num(row.type),
    status: num(row.status),
    key: row.key != null && String(row.key) !== '' ? String(row.key) : undefined,
    baseUrl,
    other: row.other != null ? String(row.other) : undefined,
    models: (row.models as string | string[]) ?? '',
    modelMapping: modelMappingRaw != null ? String(modelMappingRaw) : undefined,
    group: row.group != null && String(row.group) !== '' ? String(row.group) : 'default',
    priority: num(row.priority),
    weight: num(row.weight),
    tag,
    testTime: row.test_time != null ? num(row.test_time) : undefined,
    responseTime: row.response_time != null ? num(row.response_time) : undefined,
    usedQuota: row.used_quota != null ? num(row.used_quota) : undefined,
    balance: row.balance != null ? Number(row.balance) : undefined,
    balanceUpdatedTime:
      row.balance_updated_time != null ? num(row.balance_updated_time) : undefined,
    balanceSource: row.balance_source != null ? String(row.balance_source) : undefined,
    balanceCurrency: row.balance_currency != null ? String(row.balance_currency) : undefined,
    estimatedBalanceMicroUsd:
      row.estimated_balance_micro_usd != null ? num(row.estimated_balance_micro_usd) : undefined,
    balanceWarningThresholdMicroUsd:
      row.balance_warning_threshold_micro_usd != null
        ? num(row.balance_warning_threshold_micro_usd)
        : undefined,
    balanceCriticalThresholdMicroUsd:
      row.balance_critical_threshold_micro_usd != null
        ? num(row.balance_critical_threshold_micro_usd)
        : undefined,
    balanceAlertEnabled:
      row.balance_alert_enabled === true || row.balance_alert_enabled === 1 || row.balance_alert_enabled === '1',
    balanceAlertWebhookUrl:
      row.balance_alert_webhook_url != null && String(row.balance_alert_webhook_url) !== ''
        ? String(row.balance_alert_webhook_url)
        : undefined,
    balanceAlertLastSentAt:
      row.balance_alert_last_sent_at != null ? num(row.balance_alert_last_sent_at) : undefined,
    balanceAlertLastLevel:
      row.balance_alert_last_level != null ? String(row.balance_alert_last_level) : undefined,
    cost24hMicroUsd: row.cost_24h_micro_usd != null ? num(row.cost_24h_micro_usd) : undefined,
    estimatedDaysRemaining:
      row.estimated_days_remaining === null || row.estimated_days_remaining === undefined
        ? row.estimated_days_remaining === null
          ? null
          : undefined
        : Number(row.estimated_days_remaining),
    createdTime: row.created_time != null ? num(row.created_time) : undefined,
    channelInfo:
      channelInfoRaw && typeof channelInfoRaw === 'object'
        ? {
            is_multi_key: Boolean((channelInfoRaw as Record<string, unknown>).is_multi_key),
            isMultiKey: Boolean(
              (channelInfoRaw as Record<string, unknown>).isMultiKey ||
                (channelInfoRaw as Record<string, unknown>).IsMultiKey
            ),
          }
        : undefined,
  }
  return {
    ...channel,
    lowBalanceLevel: computeLowBalanceLevel(channel),
  }
}

/**
 * new-api `POST /api/channel` 使用 `AddChannel`，必须带 `mode`：
 * - `single`：单密钥单渠道
 * - `batch`：密钥多行，每行一个渠道（与参考前端「批量添加」一致）
 * - `multi_to_single`：多密钥进同一渠道轮询（需额外 UI 配置，此处不自动使用）
 */
function resolveAddChannelMode(key: string): { mode: 'single' | 'batch'; keyForChannel: string } {
  const lines = key.split(/\r?\n/).map((s) => s.trim()).filter(Boolean)
  if (lines.length > 1) {
    return { mode: 'batch', keyForChannel: key }
  }
  return { mode: 'single', keyForChannel: lines[0] ?? key.trim() }
}

export interface UpdateChannelParams extends Partial<CreateChannelParams> {
  id: number
}

export interface TestChannelResult {
  success: boolean
  message?: string
  time?: number
}

function buildListQuery(params: ChannelListParams): Record<string, unknown> {
  const {
    page = 1,
    page_size = 20,
    id_sort = false,
    tag_mode = false,
    status = 'all',
    type,
  } = params

  const query: Record<string, unknown> = {
    p: page,
    page_size,
    id_sort,
    tag_mode,
  }
  if (status && status !== 'all') {
    query.status = status === 'enabled' ? 'enabled' : 'disabled'
  }
  if (type !== undefined && type >= 0) {
    query.type = type
  }
  if (params.low_balance) {
    query.low_balance = 1
  }
  return query
}

/**
 * 列表或搜索统一入口：任一 keyword/group/model 非空则走 GET /api/channel/search
 */
export async function fetchChannelsPage(params?: ChannelListParams): Promise<ChannelListResponse> {
  const merged: ChannelListParams = { page: 1, page_size: 20, ...params }
  const kw = merged.keyword?.trim() ?? ''
  const g = merged.group?.trim() ?? ''
  const m = merged.model?.trim() ?? ''
  if (kw || g || m) {
    return searchChannelsApi({ ...merged, keyword: kw, group: g, model: m })
  }
  return getChannelsApi(merged)
}

/**
 * 获取渠道列表
 * GET /api/channel（分页参数 `p`，不是 `page`）
 */
export async function getChannelsApi(params?: ChannelListParams): Promise<ChannelListResponse> {
  const merged: ChannelListParams = { page: 1, page_size: 20, ...params }
  const query = buildListQuery(merged)

  const data = await get<{
    items: unknown[]
    total: number
    page?: number
    page_size?: number
    type_counts?: Record<string, number>
  }>('/api/channel', query)

  return {
    items: (data.items || []).map((row) => normalizeChannelRow(row as Record<string, unknown>)),
    total: Number(data.total ?? 0),
    page: Number(data.page ?? merged.page ?? 1),
    page_size: Number(data.page_size ?? merged.page_size ?? 20),
    type_counts: normalizeTypeCounts(data.type_counts),
  }
}

/**
 * 搜索渠道
 * GET /api/channel/search
 */
export async function searchChannelsApi(params: SearchChannelsParams): Promise<ChannelListResponse> {
  const merged: ChannelListParams = { page: 1, page_size: 20, ...params }
  const {
    page = 1,
    page_size = 20,
    keyword = '',
    group = '',
    model = '',
    id_sort = false,
    tag_mode = false,
    status = 'all',
    type,
  } = merged

  const query: Record<string, unknown> = {
    keyword,
    group,
    model,
    p: page,
    page_size,
    id_sort,
    tag_mode,
  }
  if (status && status !== 'all') {
    query.status = status === 'enabled' ? 'enabled' : 'disabled'
  }
  if (type !== undefined && type >= 0) {
    query.type = type
  }
  if (merged.low_balance) {
    query.low_balance = 1
  }

  const data = await get<{
    items: unknown[]
    total: number
    type_counts?: Record<string, number>
  }>('/api/channel/search', query)

  return {
    items: (data.items || []).map((row) => normalizeChannelRow(row as Record<string, unknown>)),
    total: Number(data.total ?? 0),
    page,
    page_size,
    type_counts: normalizeTypeCounts(data.type_counts),
  }
}

/**
 * 获取单个渠道详情
 * GET /api/channel/:id
 */
export async function getChannelDetailApi(id: number): Promise<Channel> {
  const raw = await get<unknown>(`/api/channel/${id}`)
  return normalizeChannelRow(raw as Record<string, unknown>)
}

/**
 * 获取渠道密钥（需要安全验证）
 * POST /api/channel/:id/key
 */
export async function getChannelKeyApi(id: number): Promise<{ key: string }> {
  return post(`/api/channel/${id}/key`)
}

/**
 * 创建渠道
 * POST /api/channel（成功响应无新建 id）
 */
export async function createChannelApi(data: CreateChannelParams): Promise<void> {
  const { mode, keyForChannel } = resolveAddChannelMode(data.key)
  await post('/api/channel', {
    mode,
    channel: { ...data, key: keyForChannel, models: serializeChannelModels(data.models) },
  })
}

/**
 * 更新渠道
 * PUT /api/channel — 扁平 PatchChannel，与 new-api 一致
 */
export async function updateChannelApi(data: UpdateChannelParams): Promise<void> {
  const { models, ...rest } = data
  const body: Record<string, unknown> = { ...rest }
  if (models !== undefined) {
    body.models = serializeChannelModels(models)
  }
  return put('/api/channel', body)
}

/**
 * 删除渠道
 * DELETE /api/channel/:id
 */
export async function deleteChannelApi(id: number): Promise<void> {
  return del(`/api/channel/${id}`)
}

/**
 * 批量删除渠道
 * POST /api/channel/batch
 */
export async function batchDeleteChannelsApi(ids: number[]): Promise<void> {
  return post('/api/channel/batch', { ids })
}

/**
 * 测试单个渠道
 * GET /api/channel/test/:id
 */
export async function testChannelApi(id: number): Promise<TestChannelResult> {
  return get<TestChannelResult>(`/api/channel/test/${id}`)
}

/**
 * 测试所有渠道
 * GET /api/channel/test
 */
export async function testAllChannelsApi(): Promise<void> {
  return get('/api/channel/test')
}

/**
 * 更新渠道余额
 * GET /api/channel/update_balance/:id
 */
export async function updateChannelBalanceApi(id: number): Promise<void> {
  return get(`/api/channel/update_balance/${id}`)
}

/**
 * 更新所有渠道余额
 * GET /api/channel/update_balance
 */
export async function updateAllChannelsBalanceApi(): Promise<void> {
  return get('/api/channel/update_balance')
}

/**
 * 获取渠道支持的模型列表
 * GET /api/channel/models
 */
export async function getChannelModelsApi(): Promise<string[]> {
  const data = await get<unknown[]>('/api/channel/models')
  return normalizeModelList(data)
}

/**
 * 获取启用的模型列表
 * GET /api/channel/models_enabled
 */
export async function getEnabledModelsApi(): Promise<string[]> {
  return get<string[]>('/api/channel/models_enabled')
}

/**
 * 从渠道获取上游模型列表
 * GET /api/channel/fetch_models/:id
 */
export async function fetchUpstreamModelsApi(id: number): Promise<string[]> {
  const data = await get<unknown[]>(`/api/channel/fetch_models/${id}`)
  return normalizeModelList(data)
}

/**
 * 使用尚未保存的渠道配置从上游获取模型列表
 * POST /api/channel/fetch_models
 */
export async function fetchChannelModelsByConfigApi(
  data: FetchChannelModelsByConfigParams
): Promise<string[]> {
  const result = await post<unknown[]>('/api/channel/fetch_models', {
    type: data.type,
    key: data.key,
    base_url: data.base_url,
  })
  return normalizeModelList(result)
}

/**
 * 修复渠道能力
 * POST /api/channel/fix
 */
export async function fixChannelCapabilitiesApi(): Promise<{ success: number; fails: number }> {
  return post('/api/channel/fix')
}

/**
 * 复制渠道 — 后缀与 reset_balance 为 Query 参数（与 new-api CopyChannel 一致）
 */
export async function copyChannelApi(
  id: number,
  suffix?: string,
  resetBalance?: boolean
): Promise<{ id: number }> {
  return post(`/api/channel/copy/${id}`, {}, {
    params: {
      ...(suffix !== undefined ? { suffix } : {}),
      ...(resetBalance !== undefined ? { reset_balance: resetBalance } : {}),
    },
  })
}

// ==================== 标签管理 ====================

/**
 * 批量设置渠道标签
 * POST /api/channel/batch/tag — data 为受影响数量
 */
export async function batchSetChannelTagApi(ids: number[], tag: string): Promise<number> {
  return post('/api/channel/batch/tag', { ids, tag })
}

/**
 * 禁用标签下的所有渠道
 * POST /api/channel/tag/disabled
 */
export async function disableChannelsByTagApi(tag: string): Promise<void> {
  return post('/api/channel/tag/disabled', { tag })
}

/**
 * 启用标签下的所有渠道
 * POST /api/channel/tag/enabled
 */
export async function enableChannelsByTagApi(tag: string): Promise<void> {
  return post('/api/channel/tag/enabled', { tag })
}

/**
 * 编辑标签（重命名、修改优先级等）
 * PUT /api/channel/tag
 */
export async function editChannelTagApi(
  tag: string,
  updates: { new_tag?: string; priority?: number; weight?: number }
): Promise<void> {
  return put('/api/channel/tag', { tag, ...updates })
}

/**
 * 获取标签下的模型列表
 * GET /api/channel/tag/models
 */
export async function getTagModelsApi(tag: string): Promise<string[]> {
  return get<string[]>('/api/channel/tag/models', { tag })
}

// ==================== 清理操作 ====================

/**
 * 删除所有禁用状态的渠道
 * DELETE /api/channel/disabled — data 为删除行数
 */
export async function deleteDisabledChannelsApi(): Promise<number> {
  return del('/api/channel/disabled')
}

// ==================== 余额与告警 ====================

export interface AdjustChannelBalanceParams {
  target_balance_usd?: number
  target_balance_micro_usd?: number
  reason: string
}

function normalizeBalanceAdjustment(row: Record<string, unknown>): ChannelBalanceAdjustment {
  return {
    id: num(row.id),
    channelId: num(row.channel_id ?? row.channelId),
    adminUserId: num(row.admin_user_id ?? row.adminUserId),
    reason: String(row.reason ?? ''),
    beforeMicroUsd: num(row.before_micro_usd ?? row.beforeMicroUsd),
    afterMicroUsd: num(row.after_micro_usd ?? row.afterMicroUsd),
    deltaMicroUsd: num(row.delta_micro_usd ?? row.deltaMicroUsd),
    createdAt: num(row.created_at ?? row.createdAt),
  }
}

/** POST /api/channel/:id/balance_adjust */
export async function adjustChannelBalanceApi(
  id: number,
  data: AdjustChannelBalanceParams
): Promise<ChannelBalanceAdjustment> {
  const raw = await post<Record<string, unknown>>(`/api/channel/${id}/balance_adjust`, data)
  return normalizeBalanceAdjustment((raw as Record<string, unknown>) ?? {})
}

/** POST /api/channel/:id/balance_alert/test */
export async function testChannelBalanceAlertApi(id: number): Promise<void> {
  return post(`/api/channel/${id}/balance_alert/test`)
}

/** GET /api/channel/:id/balance_ledger */
export async function getChannelBalanceLedgerApi(
  id: number,
  params?: { page?: number; page_size?: number }
): Promise<{ items: ChannelBalanceAdjustment[]; total: number }> {
  const data = await get<{ items: unknown[]; total: number }>(`/api/channel/${id}/balance_ledger`, params)
  return {
    items: (data.items || []).map((row) => normalizeBalanceAdjustment(row as Record<string, unknown>)),
    total: Number(data.total ?? 0),
  }
}

function normalizeChannelCostLog(row: Record<string, unknown>): ChannelCostLog {
  return {
    id: num(row.id),
    channelId: num(row.channel_id ?? row.channelId),
    requestId: String(row.request_id ?? row.requestId ?? ''),
    modelName: String(row.model_name ?? row.modelName ?? ''),
    promptTokens: num(row.prompt_tokens ?? row.promptTokens),
    completionTokens: num(row.completion_tokens ?? row.completionTokens),
    userQuota: num(row.user_quota ?? row.userQuota),
    userCostMicroUsd: num(row.user_cost_micro_usd ?? row.userCostMicroUsd),
    channelCostMicroUsd: num(row.channel_cost_micro_usd ?? row.channelCostMicroUsd),
    costSource: String(row.cost_source ?? row.costSource ?? ''),
    balanceAfterMicroUsd: num(row.balance_after_micro_usd ?? row.balanceAfterMicroUsd),
    createdAt: num(row.created_at ?? row.createdAt),
  }
}

function normalizeChannelOpsOverview(raw: Record<string, unknown>): ChannelOpsOverview {
  const topCost = Array.isArray(raw.top_cost_channels) ? raw.top_cost_channels : []
  const depleting = Array.isArray(raw.depleting_soon_channels) ? raw.depleting_soon_channels : []
  return {
    lowBalanceWarningCount: num(raw.low_balance_warning_count),
    lowBalanceCriticalCount: num(raw.low_balance_critical_count),
    cost24hTotalMicroUsd: num(raw.cost_24h_total_micro_usd),
    topCostChannels: topCost.map((row) => {
      const r = row as Record<string, unknown>
      return {
        channelId: num(r.channel_id ?? r.channelId),
        channelName: String(r.channel_name ?? r.channelName ?? ''),
        cost24hMicroUsd: num(r.cost_24h_micro_usd ?? r.cost24hMicroUsd),
      }
    }),
    depletingSoonChannels: depleting.map((row) => {
      const r = row as Record<string, unknown>
      return {
        channelId: num(r.channel_id ?? r.channelId),
        channelName: String(r.channel_name ?? r.channelName ?? ''),
        estimatedDaysRemaining: Number(r.estimated_days_remaining ?? r.estimatedDaysRemaining ?? 0),
        balanceMicroUsd: num(r.balance_micro_usd ?? r.balanceMicroUsd),
        cost24hMicroUsd: num(r.cost_24h_micro_usd ?? r.cost24hMicroUsd),
        lowBalanceLevel: String(r.low_balance_level ?? r.lowBalanceLevel ?? ''),
      }
    }),
  }
}

export interface BatchChannelBalanceAlertParams {
  ids?: number[]
  tag?: string
  balance_source?: string
  balance_warning_threshold_usd?: number
  balance_critical_threshold_usd?: number
  balance_alert_enabled?: boolean
  balance_alert_webhook_url?: string
}

/** GET /api/channel/:id/cost_logs */
export async function getChannelCostLogsApi(
  id: number,
  params?: { page?: number; page_size?: number }
): Promise<{ items: ChannelCostLog[]; total: number }> {
  const data = await get<{ items: unknown[]; total: number }>(`/api/channel/${id}/cost_logs`, params)
  return {
    items: (data.items || []).map((row) => normalizeChannelCostLog(row as Record<string, unknown>)),
    total: Number(data.total ?? 0),
  }
}

/** GET /api/channel/ops/overview */
export async function getChannelOpsOverviewApi(): Promise<ChannelOpsOverview> {
  const raw = await get<Record<string, unknown>>('/api/channel/ops/overview')
  return normalizeChannelOpsOverview(raw ?? {})
}

/** POST /api/channel/batch/balance_alert 或 /api/channel/tag/balance_alert */
export async function batchSetChannelBalanceAlertApi(
  params: BatchChannelBalanceAlertParams
): Promise<number | void> {
  const { ids, tag, ...rest } = params
  if (tag?.trim()) {
    await post('/api/channel/tag/balance_alert', { tag: tag.trim(), ...rest })
    return
  }
  return post<number>('/api/channel/batch/balance_alert', { ids, ...rest })
}
